import { useEffect, useLayoutEffect, useRef } from "react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import type { DemoStatus } from "./types";

export function Shell({ crumb, title, eyebrow, actions, children }: {
  crumb?: boolean; title?: ReactNode; eyebrow?: string; actions?: ReactNode; children: ReactNode;
}) {
  return (
    <div className="min-h-screen flex flex-col bg-ground">
      {/* WRAPS RATHER THAN OVERFLOWS. On a phone the title takes its own line under the
          brand and the crumb, and the actions - four of them on Review - drop to the line
          below that; on one row they ran off the right edge, Publish first. */}
      <header className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3 px-4 sm:px-6 md:px-10 py-3 sm:py-4 border-b border-line bg-paper">
        <div className="flex flex-wrap items-center gap-x-7 gap-y-2 min-w-0">
          <Link to="/" className="font-semibold text-[16px] tracking-[0.01em]">Demo creator</Link>
          {crumb && <Link to="/" className="text-[14px] text-muted">← All demos</Link>}
          {title && (
            <div className="flex flex-col gap-0.5 min-w-0 basis-full sm:basis-auto">
              {eyebrow && <div className="text-[11px] text-muted uppercase tracking-[0.08em]">{eyebrow}</div>}
              <div className="text-[15px] font-medium truncate">{title}</div>
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">{actions}</div>
      </header>
      {children}
    </div>
  );
}

export function Card({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <div className={`flex flex-col bg-paper border border-line rounded-card overflow-hidden ${className}`}>{children}</div>;
}

/* The title's 12rem basis is what decides the wrap: a sentence of hint, or a hint and a
 * button, cannot fit beside it on a phone and drops to its own line, while a close × can
 * and stays put. Without the basis the title would claim its whole text width first and
 * push even the × underneath. */
export function CardHead({ title, hint, right }: { title: ReactNode; hint?: ReactNode; right?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-2 px-5 py-3.5 border-b border-line-soft">
      <div className="flex-1 basis-48 min-w-0 text-[14px] font-semibold">{title}</div>
      {right ?? (hint && <div className="text-[12px] text-muted sm:text-right">{hint}</div>)}
    </div>
  );
}

/**
 * THE FIRST MODAL IN THIS CONSOLE, and a native <dialog> rather than a fixed-inset div,
 * because everything a hand-rolled overlay would have to re-implement - the focus trap,
 * Esc, making the page behind inert, handing focus back to whatever opened it, sitting
 * above every stacking context without a z-index auction - the platform already does, and
 * does correctly.
 *
 * NO DISPLAY UTILITY ON THE <dialog> ITSELF. A closed dialog is hidden by the browser's own
 * `dialog:not([open]) { display: none }`, and an author declaration beats the UA stylesheet
 * whatever cascade layer it is in - so a `flex` in this class list would leave the modal on
 * screen, open or not. The dialog stays `display: block`; the panel inside is what does the
 * column.
 *
 * MOUNTED ONLY WHILE OPEN, which is also how the audio stops: unmounting the player pauses
 * its element and revokes its object URL in the cleanup it already has.
 */
export function Modal({ onClose, labelledBy, width = "w-[min(920px,calc(100vw-2rem))]", children }: {
  onClose: () => void; labelledBy: string;
  /** A width utility for the dialog. The call view wants room; a question does not. */
  width?: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const fromBackdrop = useRef(false);

  useEffect(() => {
    ref.current?.showModal();
    // `showModal` makes the page behind inert, not still.
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = previous; };
  }, []);

  return (
    <dialog
      ref={ref}
      aria-labelledby={labelledBy}
      /* Esc fires `cancel` then `close` on the element itself, without asking React - so
       * `close` is what calls back, and the DOM and the caller's state cannot drift apart. */
      onClose={onClose}
      /* The backdrop IS this element's own box. With `p-0` every click inside the panel
       * lands on a child, so a click whose target is the dialog came from outside it -
       * but only if the press STARTED there too. Dragging across a transcript to select
       * it and releasing past the panel's edge is a click on the dialog, and closing the
       * thing somebody was in the middle of copying is the rudest possible reading of it. */
      onMouseDown={(e) => { fromBackdrop.current = e.target === ref.current; }}
      onClick={(e) => { if (fromBackdrop.current && e.target === ref.current) onClose(); }}
      className={`m-auto p-0 border-0 bg-transparent ${width} max-h-[calc(100dvh-2rem)] sm:max-h-[calc(100dvh-4rem)]`}
    >
      <div className="flex flex-col max-h-[calc(100dvh-2rem)] sm:max-h-[calc(100dvh-4rem)] bg-paper border border-line rounded-card overflow-hidden">
        {children}
      </div>
    </dialog>
  );
}

export function Note({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5 px-5 py-3.5 bg-paper-soft border border-dashed border-field rounded-card text-[12px] leading-relaxed text-muted">
      <div className="font-medium text-ink">{title}</div>
      <div>{children}</div>
    </div>
  );
}

type ButtonProps = {
  kind?: "primary" | "secondary" | "quiet" | "danger";
  disabled?: boolean;
  onClick?: () => void;
  type?: "button" | "submit";
  children: ReactNode;
  className?: string;
};

export function Button({ kind = "secondary", disabled, onClick, type = "button", children, className = "" }: ButtonProps) {
  const base = "inline-flex items-center gap-2 h-11 px-4 rounded-lg text-[14px] font-medium transition-colors";
  const look = disabled
    ? "bg-disabled text-muted cursor-not-allowed"
    : kind === "primary"
      ? "bg-ink text-white hover:bg-ink-soft"
      : kind === "quiet"
        ? "text-muted hover:text-ink"
        : kind === "danger"
          ? "bg-paper text-warn border border-field hover:border-warn hover:bg-warn-wash"
          : "bg-paper text-ink border border-field hover:border-ink";
  return (
    <button type={type} disabled={disabled} onClick={onClick} className={`${base} ${look} ${className}`}>
      {children}
    </button>
  );
}

/** Expiry is derived, never written back, so "has it run out?" is a question, not a status. */
export const lapsed = (expiresAt?: number | null) =>
  expiresAt != null && expiresAt <= Date.now();

/* A two-word switch - Off | On - for a setting small enough that a button and a dialog
 * would be heavier than the thing being set. The selected half is filled, the other is
 * quiet; both halves are always readable, so the current state needs no label. */
export function Toggle({ value, onChange }: { value: boolean; onChange: (next: boolean) => void }) {
  return (
    <span className="inline-flex p-0.5 bg-paper-soft border border-field rounded-lg">
      {[false, true].map((state) => (
        <button
          key={String(state)}
          type="button"
          aria-pressed={value === state}
          onClick={() => { if (value !== state) onChange(state); }}
          className={`px-2.5 py-1 rounded-[6px] text-[12px] font-medium transition-colors ${
            value === state ? "bg-ink text-white" : "text-muted hover:text-ink"
          }`}
        >
          {state ? "On" : "Off"}
        </button>
      ))}
    </span>
  );
}

export function StatusPill({ status, pending, expiresAt }: { status: DemoStatus; pending?: number; expiresAt?: number | null }) {
  let label: string = status;
  let tone = "bg-chip text-muted";
  let dot = "bg-faint";
  if (status === "live") {
    // Expiry is derived at read time and never written back, so a lapsed demo is still a
    // 'live' row. Say so plainly here, or the console counts down to "0 days left" and
    // sits there while the prospect is being shown the closed page.
    if (lapsed(expiresAt)) {
      label = "Closed";
    } else {
      const days = expiresAt ? Math.ceil((expiresAt - Date.now()) / 86_400_000) : null;
      label = days !== null && days <= 10 ? `Live · ${days} days left` : "Live";
      tone = "bg-ok-wash text-ok"; dot = "bg-ok";
    }
  } else if (status === "review") {
    label = pending ? `${pending} fact${pending === 1 ? "" : "s"} to check` : "Ready to publish";
    tone = pending ? "bg-warn-wash text-warn" : "bg-ok-wash text-ok";
    dot = pending ? "bg-warn" : "bg-ok";
  } else if (status === "drafting") {
    label = "Drafting…"; tone = "bg-warn-wash text-warn"; dot = "bg-warn";
  } else if (status === "promoted") {
    label = "Promoted"; tone = "bg-promoted-wash text-promoted"; dot = "bg-promoted";
  } else if (status === "paused") {
    label = "Paused";
  } else if (status === "expired") {
    label = "Expired";
  } else if (status === "draft") {
    label = "Draft";
  }
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[12px] font-medium ${tone}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
      {label}
    </span>
  );
}

export const inputClass =
  "h-10 px-3 text-[14px] border border-field rounded-lg bg-paper text-ink box-border w-full focus:outline-none focus:border-ink";
export const textareaClass =
  "px-3 py-2.5 text-[14px] leading-relaxed border border-field rounded-lg bg-paper text-ink resize-none box-border w-full focus:outline-none focus:border-ink";

/**
 * A textarea as tall as its text. The review screen is for READING what the assistant
 * will say and then deciding, so a sentence that runs past the edge of its box is the one
 * thing the screen must not do - and a single-line <input> does exactly that: it holds the
 * text at full width and scrolls it under the caret, so a 200-character fact could only be
 * read by arrowing through it.
 *
 * Height is set from scrollHeight in a layout effect (before paint, so it never flashes at
 * one row) and again on every edit. `rows={1}` is the floor; the reset to "auto" is what
 * lets it SHRINK when text is deleted.
 */
export function GrowingTextarea(
  { value, onChange, className = "", ...rest }:
  { value: string; onChange: (value: string) => void; className?: string } &
  Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, "value" | "onChange" | "className">,
) {
  const ref = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);
  return (
    <textarea
      ref={ref}
      rows={1}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={`resize-none overflow-hidden box-border w-full ${className}`}
      {...rest}
    />
  );
}

export function Field({ label, hint, children }: { label: ReactNode; hint?: ReactNode; children: ReactNode }) {
  return (
    <label className="flex flex-col gap-2">
      <span className="text-[13px] font-medium">{label}</span>
      {children}
      {hint && <span className="text-[12px] text-muted">{hint}</span>}
    </label>
  );
}

export function when(ms: number | null | undefined): string {
  if (!ms) return "–";
  const d = new Date(ms);
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
  const time = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  if (sameDay) return `Today, ${time}`;
  if (d.toDateString() === yesterday.toDateString()) return `Yesterday, ${time}`;
  return d.toLocaleDateString([], { day: "numeric", month: "short" }) + `, ${time}`;
}

export function duration(ms: number | null | undefined): string {
  if (!ms) return "–";
  const s = Math.round(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

export function longDate(ms: number | null | undefined): string {
  if (!ms) return "–";
  return new Date(ms).toLocaleDateString([], { day: "numeric", month: "short", year: "numeric" });
}
